import { useEffect, useRef, useCallback, useState } from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { useDebouncedCallback } from 'use-debounce'
import { datadogLogs } from '@datadog/browser-logs'

export type WebSocketSubscriptionState = 'connecting' | 'connected' | 'disconnected' | 'error'

interface UseWebSocketSubscriptionOptions {
    tokenQuery: any
    onMessage: (event: MessageEvent) => void
    onReset?: () => void
    onError?: (error: Error) => void
}

export interface WebSocketSubscriptionResult {
    state: WebSocketSubscriptionState
    error: Error | null
    getLastMessageAt: () => number | null
    forceReconnect: (reason?: string) => void
}

export function useWebSocketSubscription({
    tokenQuery,
    onMessage,
    onReset,
    onError,
}: UseWebSocketSubscriptionOptions): WebSocketSubscriptionResult {
    const [rawWsUrl, setRawWsUrl] = useState<string | null>(null)
    const [state, setState] = useState<WebSocketSubscriptionState>('disconnected')
    const [error, setError] = useState<Error | null>(null)
    const isRefreshingRef = useRef(false)
    const lastMessageAtRef = useRef<number | null>(null)

    const debouncedSetRawWsUrl = useDebouncedCallback((newUrl: string) => {
        setRawWsUrl((prev) => (prev !== newUrl ? newUrl : prev))
    }, 300)

    const buildWebSocketUrl = useCallback((tokenValue: string | undefined) => {
        if (!tokenValue) return null
        const baseUrl = process.env.NEXT_PUBLIC_INNGEST_API_BASE_URL
        const wsBaseUrl =
            process.env.NODE_ENV === 'development' ? baseUrl?.replace('http', 'ws') : baseUrl?.replace('https', 'wss')
        return `${wsBaseUrl}/v1/realtime/connect?token=${tokenValue}`
    }, [])

    const handleError = useCallback(
        (error: Error) => {
            setError(error)
            onError?.(error)
            setState('error')
        },
        [onError]
    )

    const refreshToken = useCallback(async () => {
        if (isRefreshingRef.current) {
            return null
        }
        isRefreshingRef.current = true
        try {
            const result = await tokenQuery.refetch()
            const refreshedToken = result.data
            if (refreshedToken) {
                const url = buildWebSocketUrl(refreshedToken.key)
                if (url) debouncedSetRawWsUrl(url)
                return refreshedToken
            }
            return refreshedToken
        } catch (err) {
            handleError(err as Error)
            return null
        } finally {
            isRefreshingRef.current = false
        }
    }, [tokenQuery, buildWebSocketUrl, debouncedSetRawWsUrl, handleError])

    const debouncedRefreshToken = useDebouncedCallback(refreshToken, 2000, { maxWait: 5000 })

    useEffect(() => {
        if (tokenQuery.data) {
            const url = buildWebSocketUrl(tokenQuery.data.key)
            if (url) debouncedSetRawWsUrl(url)
        }
    }, [tokenQuery.data, buildWebSocketUrl, debouncedSetRawWsUrl])

    useEffect(() => {
        if (rawWsUrl && onReset) {
            onReset()
        }
    }, [rawWsUrl, onReset])

    useEffect(() => {
        if (!tokenQuery.data && !tokenQuery.isLoading && !tokenQuery.error && tokenQuery.fetchStatus !== 'idle') {
            tokenQuery.refetch()
        }
    }, [tokenQuery])

    const wrappedOnMessage = useCallback(
        (event: MessageEvent) => {
            lastMessageAtRef.current = Date.now()
            onMessage(event)
        },
        [onMessage]
    )

    const handleClose = useCallback(
        (event: CloseEvent) => {
            if ([4001, 4003, 1006].includes(event.code)) {
                debouncedRefreshToken()
            } else if (event.code !== 1000) {
                handleError(new Error(`WebSocket closed unexpectedly: ${event.reason || event.code}`))
            }
            setState('disconnected')
        },
        [debouncedRefreshToken, handleError]
    )

    const { readyState, getWebSocket } = useWebSocket(
        rawWsUrl,
        {
            onMessage: wrappedOnMessage,
            onError: () => handleError(new Error('WebSocket connection error')),
            onClose: handleClose,
            shouldReconnect: (closeEvent) => {
                const handledManually = [4001, 4003, 1006].includes(closeEvent.code)
                return closeEvent.code !== 1000 && !handledManually
            },
            reconnectAttempts: 10,
            reconnectInterval: (attempt) => Math.min(Math.pow(2, attempt) * 1000, 30000),
            share: true,
        },
        !!rawWsUrl
    )

    const getLastMessageAt = useCallback(() => lastMessageAtRef.current, [])

    const forceReconnect = useCallback(
        (reason?: string) => {
            const ws = getWebSocket()
            if (ws && ws.readyState === WebSocket.OPEN) {
                datadogLogs.logger.info('ws_subscription.force_reconnect', {
                    feature: 'ws-subscription',
                    reason: reason ?? 'force-reconnect',
                })
                ;(ws as WebSocket).close(4100, reason ?? 'force-reconnect')
            }
        },
        [getWebSocket]
    )

    useEffect(() => {
        if (readyState === ReadyState.CONNECTING) {
            setState('connecting')
        } else if (readyState === ReadyState.OPEN) {
            setState('connected')
            setError(null)
        } else if (readyState === ReadyState.CLOSING || readyState === ReadyState.CLOSED) {
            setState('disconnected')
        }
    }, [readyState])

    return { state, error, getLastMessageAt, forceReconnect }
}
